import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { aiApi } from '../../utils/aiApi';

export interface AIAssistant {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'inactive' | 'training';
  createdAt: string;
  updatedAt: string;
  model?: string;
  instructions?: string;
  tools?: string[];
}

interface AIAssistantState {
  assistants: AIAssistant[];
  currentAssistant: AIAssistant | null;
  loading: boolean;
  error: string | null;
}

const initialState: AIAssistantState = {
  assistants: [],
  currentAssistant: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchAIAssistants = createAsyncThunk(
  'aiAssistants/fetchAll',
  async () => {
    const response = await aiApi.get('/assistants');
    return response.data;
  }
);

export const createAIAssistant = createAsyncThunk(
  'aiAssistants/create',
  async (assistantData: Partial<AIAssistant>) => {
    const response = await aiApi.post('/assistants', assistantData);
    return response.data;
  }
);

export const updateAIAssistant = createAsyncThunk(
  'aiAssistants/update',
  async ({ id, data }: { id: string; data: Partial<AIAssistant> }) => {
    const response = await aiApi.put(`/assistants/${id}`, data);
    return response.data;
  }
);

const aiAssistantSlice = createSlice({
  name: 'aiAssistants',
  initialState,
  reducers: {
    setCurrentAssistant: (state, action) => {
      state.currentAssistant = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch assistants
      .addCase(fetchAIAssistants.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAIAssistants.fulfilled, (state, action) => {
        state.loading = false;
        state.assistants = action.payload;
      })
      .addCase(fetchAIAssistants.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch assistants';
      })
      // Create assistant
      .addCase(createAIAssistant.fulfilled, (state, action) => {
        state.assistants.push(action.payload);
      })
      // Update assistant
      .addCase(updateAIAssistant.fulfilled, (state, action) => {
        const index = state.assistants.findIndex(a => a.id === action.payload.id);
        if (index !== -1) {
          state.assistants[index] = action.payload;
        }
      });
  },
});

export const { setCurrentAssistant, clearError } = aiAssistantSlice.actions;
export default aiAssistantSlice.reducer;